import classNames from "classnames";
import * as React from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaSmallSpinner from "../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallBoldText from "../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import TestRunnerMethod from "./test-runner-method";
import { ITestRunnerProps } from "./test-runner.types";
import { useTestRunner } from "./use-test-runner";
import "./test-runner.scss";

export type { ITestRunnerMethod, ITestRunnerParameter, ITestRunnerProps } from "./test-runner.types";

const DEFAULT_EMPTY_MESSAGE: string = "No methods found. Write a class with static methods and open this panel.";

/**
 * Collapsible "Test Console" docked at the bottom of the datasource code-action editors. Lists the
 * code's callable methods and runs them with ad-hoc arguments. Composition root: state lives in
 * `useTestRunner`, each method renders through `TestRunnerMethod`.
 */
const TestRunner: React.FC<ITestRunnerProps> = (props) => {
  const showServerClientSplit: boolean = props.showServerClientSplit ?? true;
  const runButtonLabel: string = props.runButtonLabel ?? "Run";
  const emptyMessage: string = props.emptyMessage ?? DEFAULT_EMPTY_MESSAGE;

  const controller = useTestRunner(props);
  const methodCount: number = controller.methods.length;

  return (
    <div className={classNames("kuika-test-runner", { "kuika-test-runner--open": controller.open })}>
      <div className="kuika-test-runner__header" onClick={controller.toggleOpen}>
        <div className="kuika-test-runner__header-left">
          <KuikaSmallBoldText className="kuika-test-runner__title">Test Console</KuikaSmallBoldText>
          {methodCount > 0 && (
            <KuikaSmallText className="kuika-test-runner__count">
              {methodCount} {methodCount === 1 ? "method" : "methods"}
            </KuikaSmallText>
          )}
        </div>
        <div className="kuika-test-runner__header-right">
          {controller.open && (
            <KuikaButton
              type="text"
              size="sm"
              onClick={(e?: React.MouseEvent) => {
                e?.stopPropagation();
                controller.refresh();
              }}
            >
              <span className="kuika-test-runner__refresh">
                <KuikaSvg name="refresh" width={14} height={14} className="kuika-test-runner__refresh-icon" />
                Refresh
              </span>
            </KuikaButton>
          )}
          <KuikaSvg
            name="chevron-down"
            type="arrows"
            width={14}
            height={14}
            className={classNames("kuika-test-runner__chevron", {
              "kuika-test-runner__chevron--open": controller.open
            })}
          />
        </div>
      </div>

      {controller.open && (
        <div className="kuika-test-runner__body">
          {controller.loading && (
            <div className="kuika-test-runner__loading">
              <KuikaSmallSpinner />
              <KuikaSmallText className="kuika-test-runner__loading-text">Loading methods…</KuikaSmallText>
            </div>
          )}
          {!controller.loading && methodCount === 0 && (
            <KuikaEmptyState svgName="bolt" customName="No testable methods" description={emptyMessage} />
          )}
          {!controller.loading &&
            methodCount > 0 &&
            controller.methods.map((method) => (
              <TestRunnerMethod
                key={method.name}
                method={method}
                isSelected={controller.selectedMethod === method.name}
                output={controller.selectedMethod === method.name ? controller.output : ""}
                running={controller.selectedMethod === method.name && controller.running}
                runningSide={controller.selectedMethod === method.name ? controller.runningSide : null}
                showServerClientSplit={showServerClientSplit}
                runButtonLabel={runButtonLabel}
                paramValue={controller.paramValue}
                onSelect={controller.selectMethod}
                onParamChange={controller.setParam}
                onRun={controller.run}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export { TestRunner };
